import { useEffect, useMemo, useState } from 'react';
import type { DiseaseScore, DiseaseSeverity } from '@greeniq/shared';
import { getCurrentDiseaseRisk, getDiseaseHistory } from '../api/disease.js';
import { DataCard } from './ui/data-card';

interface Props {
  courseSlug: string;
}

const DISEASE_LABEL: Record<string, string> = {
  dollar_spot: 'Dollar spot',
  brown_patch: 'Brown patch',
  pythium: 'Pythium blight',
  anthracnose: 'Anthracnose',
  snow_mold: 'Snow mold',
};

const DISEASE_SHORT_HINT: Record<string, string> = {
  dollar_spot: 'Smith-Kerns (RH + temp, 5-day mean)',
  brown_patch: 'Three-legged rule (night temp, LW, canopy RH)',
  pythium: 'Three-legged rule (higher thresholds than brown patch)',
  anthracnose: 'Heat + dry + muggy nights (5-day composite)',
  snow_mold: 'Autumn soil priming (Sep–Nov only)',
};

// Dark-theme severity palette — align with semantic tokens.
const SEVERITY_COLOR: Record<DiseaseSeverity, string> = {
  low: 'var(--color-success)',
  moderate: 'var(--color-warning)',
  high: 'var(--color-error)',
};

export function DiseaseRiskCard({ courseSlug }: Props) {
  const [current, setCurrent] = useState<DiseaseScore[] | null>(null);
  const [history, setHistory] = useState<DiseaseScore[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, h] = await Promise.all([
          getCurrentDiseaseRisk(courseSlug),
          getDiseaseHistory(courseSlug, 14),
        ]);
        if (cancelled) return;
        setCurrent(c.scores);
        setHistory(h.history);
      } catch {
        // silent — card hides when data is missing
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseSlug]);

  const historyByDisease = useMemo(() => {
    const map: Record<string, DiseaseScore[]> = {};
    for (const s of history ?? []) {
      (map[s.disease] ||= []).push(s);
    }
    return map;
  }, [history]);

  if (!current || current.length === 0) return null;

  return (
    <DataCard
      className="mt-6"
      title="Disease risk"
      subtitle="Refreshed daily with the weather cron"
    >
      <div className="grid grid-cols-1 gap-2">
        {current.map((score) => (
          <DiseaseTile
            key={score.disease}
            score={score}
            history={historyByDisease[score.disease] ?? []}
          />
        ))}
      </div>
    </DataCard>
  );
}

function DiseaseTile({ score, history }: { score: DiseaseScore; history: DiseaseScore[] }) {
  const label = DISEASE_LABEL[score.disease] ?? score.disease;
  const hint = DISEASE_SHORT_HINT[score.disease] ?? '';
  const color = SEVERITY_COLOR[score.severity];
  const probability = score.probability ?? 0;

  return (
    <div className="overflow-hidden rounded-sm border border-border bg-bg-elevated p-2.5">
      <div className="flex items-baseline justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-fg">{label}</div>
          <div className="truncate text-[10px] text-fg-muted">{hint}</div>
        </div>
        <div className="shrink-0 text-right">
          <span className="text-base font-semibold" style={{ color }}>
            {(probability * 100).toFixed(0)}%
          </span>
          <span
            className="ml-1 text-[10px] uppercase tracking-[0.08em]"
            style={{ color }}
          >
            {score.severity}
          </span>
        </div>
      </div>
      {history.length >= 2 && <Sparkline history={history} color={color} />}
    </div>
  );
}

function Sparkline({ history, color }: { history: DiseaseScore[]; color: string }) {
  const values = history.map((h) => h.probability ?? 0);
  const w = 100;
  const h = 20;
  const max = Math.max(...values, 0.05);
  const min = Math.min(...values, 0);
  const range = Math.max(max - min, 0.01);
  const points = values
    .map((v, i) => {
      const x = (i / Math.max(values.length - 1, 1)) * w;
      const y = h - ((v - min) / range) * h;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg
      className="mt-1.5 block w-full"
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      aria-hidden
    >
      <polyline
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
